import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Briefcase,
  Settings,
  UserCog,
  ShieldCheck,
  Shield,
  ChevronDown,
  Cog,
  Layers,
  ScrollText,
} from 'lucide-react';
import { useAuthStore } from '@/store';
import { useUIStore } from '@/store/useUIStore';
import { usePlatformNavStore } from '@/store/usePlatformNavStore';
import { PlatformSidebarNav } from './PlatformSidebarNav';
import { BrandLogo } from './BrandLogo';
import { cn } from '@/lib/utils';
import { hasAnyPermission, hasPermission } from '@/lib/permission-match';
import { menuPathAllowedByRoleConfig } from '@/lib/route-access';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  /** 所需权限码（与后端一致；platform:* 可覆盖 platform: 前缀）；多值时为「任一」 */
  permission?: string | string[];
  /** 凡运营后台账号（role=admin）均可见，如权限说明页 */
  allPlatformAdmins?: boolean;
}

function menuAllowed(
  perms: string[],
  item: MenuItem,
  isPlatformAdmin: boolean,
): boolean {
  if (item.allPlatformAdmins && isPlatformAdmin) return true;
  const req = item.permission;
  if (req === undefined) return false;
  if (Array.isArray(req)) {
    return hasAnyPermission(perms, req);
  }
  return hasPermission(perms, req);
}

const makeLinkClass =
  (isLight: boolean) =>
  ({ isActive }: { isActive: boolean }) =>
    cn(
      'mx-3 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 motion-reduce:transition-none',
      isActive
        ? isLight
          ? 'border border-cyan-500/35 bg-cyan-100/70 text-cyan-900 shadow-[0_6px_20px_-16px_rgba(8,145,178,0.45)]'
          : 'border border-cyan-500/25 bg-cyan-500/10 text-cyan-100 shadow-[0_0_32px_-12px_rgba(34,211,238,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]'
        : isLight
          ? 'border border-transparent text-slate-600 hover:border-cyan-500/20 hover:bg-white hover:text-slate-900'
          : 'border border-transparent text-zinc-400 hover:border-white/5 hover:bg-white/[0.04] hover:text-zinc-100',
    );

const PLATFORM_MAIN_ITEMS: MenuItem[] = [
  {
    path: '/dashboard',
    label: '数据概览',
    icon: <LayoutDashboard className="h-5 w-5" />,
    permission: 'platform:dashboard:view',
  },
  {
    path: '/order',
    label: '订单管理',
    icon: <ShoppingCart className="h-5 w-5" />,
    permission: 'platform:order:view',
  },
  {
    path: '/tenant',
    label: '租户管理',
    icon: <Users className="h-5 w-5" />,
    permission: ['platform:tenant:view', 'platform:tenant:manage'],
  },
  {
    path: '/market-config',
    label: '应用市场配置',
    icon: <Settings className="h-5 w-5" />,
    permission: 'platform:market:mutate',
  },
  {
    path: '/plan-config',
    label: '订阅版本配置',
    icon: <Briefcase className="h-5 w-5" />,
    permission: 'platform:market:mutate',
  },
];

/** 系统管理：平台角色、平台用户、权限与菜单 */
const PLATFORM_SYSTEM_ITEMS: MenuItem[] = [
  {
    path: '/platform-roles',
    label: '平台角色',
    icon: <Layers className="h-5 w-5" />,
    permission: 'platform:platform_user:manage',
  },
  {
    path: '/platform-users',
    label: '平台用户',
    icon: <UserCog className="h-5 w-5" />,
    permission: 'platform:platform_user:manage',
  },
  {
    path: '/audit-logs',
    label: '审计日志',
    icon: <ScrollText className="h-5 w-5" />,
    permission: 'platform:audit:view',
  },
  {
    path: '/rbac',
    label: '权限与菜单',
    icon: <ShieldCheck className="h-5 w-5" />,
    allPlatformAdmins: true,
  },
];

const PLATFORM_SYSTEM_PATHS = PLATFORM_SYSTEM_ITEMS.map((i) => i.path);

const TENANT_MAIN_ITEMS: MenuItem[] = [
  {
    path: '/workspace',
    label: '工作台总览',
    icon: <LayoutDashboard className="h-5 w-5" />,
    permission: 'tenant:workspace:view',
  },
  {
    path: '/market',
    label: '应用市场',
    icon: <ShoppingCart className="h-5 w-5" />,
    permission: 'tenant:market:view',
  },
];

/** 系统管理：成员、租户角色、审计 */
const TENANT_SYSTEM_ITEMS: MenuItem[] = [
  {
    path: '/team',
    label: '成员与账号',
    icon: <Users className="h-5 w-5" />,
    permission: ['tenant:user:view', 'tenant:user:manage'],
  },
  {
    path: '/team/roles',
    label: '角色与权限',
    icon: <Shield className="h-5 w-5" />,
    permission: ['tenant:user:view', 'tenant:user:manage'],
  },
  {
    path: '/team/audit-logs',
    label: '审计日志',
    icon: <ScrollText className="h-5 w-5" />,
    permission: ['tenant:user:view', 'tenant:user:manage'],
  },
];

const TENANT_SYSTEM_PATHS = TENANT_SYSTEM_ITEMS.map((i) => i.path);

function tenantSystemSectionActive(pathname: string): boolean {
  return TENANT_SYSTEM_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const { user } = useAuthStore();
  const theme = useUIStore((s) => s.theme);
  const isLight = theme === 'light';
  const linkClass = makeLinkClass(isLight);
  const perms = user?.permissions ?? [];
  const menuKeys = user?.menuKeys;
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const navLoadStatus = usePlatformNavStore((s) => s.loadStatus);
  const platformSidebarTree = usePlatformNavStore((s) => s.sidebarTree);
  const useDynamicPlatformNav =
    isAdmin &&
    navLoadStatus === 'ready' &&
    platformSidebarTree != null &&
    platformSidebarTree.length > 0;

  const mainVisible = PLATFORM_MAIN_ITEMS.filter(
    (item) =>
      menuAllowed(perms, item, isAdmin) &&
      menuPathAllowedByRoleConfig(item.path, menuKeys),
  );
  const systemVisible = PLATFORM_SYSTEM_ITEMS.filter(
    (item) =>
      menuAllowed(perms, item, isAdmin) &&
      menuPathAllowedByRoleConfig(item.path, menuKeys),
  );

  const systemSectionActive = PLATFORM_SYSTEM_PATHS.some(
    (p) =>
      location.pathname === p || location.pathname.startsWith(`${p}/`),
  );

  const [systemMgmtOpen, setSystemMgmtOpen] = useState(true);
  const [tenantSystemMgmtOpen, setTenantSystemMgmtOpen] = useState(true);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    const inSystem = (path: string) =>
      PLATFORM_SYSTEM_PATHS.some(
        (p) => path === p || path.startsWith(`${p}/`),
      );
    const wasIn = inSystem(prev);
    const nowIn = inSystem(location.pathname);
    if (nowIn && !wasIn) {
      setSystemMgmtOpen(true);
    }
  }, [location.pathname]);

  const prevTenantPathRef = useRef(location.pathname);

  useEffect(() => {
    if (isAdmin) return;
    const prev = prevTenantPathRef.current;
    prevTenantPathRef.current = location.pathname;
    const inTenantSys = (path: string) =>
      TENANT_SYSTEM_PATHS.some(
        (p) => path === p || path.startsWith(`${p}/`),
      );
    const wasIn = inTenantSys(prev);
    const nowIn = inTenantSys(location.pathname);
    if (nowIn && !wasIn) {
      setTenantSystemMgmtOpen(true);
    }
  }, [location.pathname, isAdmin]);

  const tenantMainVisible = TENANT_MAIN_ITEMS.filter(
    (item) =>
      menuAllowed(perms, item, isAdmin) &&
      menuPathAllowedByRoleConfig(item.path, menuKeys),
  );
  const tenantSystemVisible = TENANT_SYSTEM_ITEMS.filter(
    (item) =>
      menuAllowed(perms, item, isAdmin) &&
      menuPathAllowedByRoleConfig(item.path, menuKeys),
  );

  const tenantSystemActive = tenantSystemSectionActive(location.pathname);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex h-dvh w-64 flex-col border-r lg:static lg:h-full lg:min-h-0 lg:shrink-0',
          isLight
            ? 'border-slate-300/90 bg-slate-50'
            : 'border-cyan-500/15 bg-zinc-950',
          'transform transition-transform duration-300 ease-out motion-reduce:transition-none',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div
          className={cn(
            'pointer-events-none absolute inset-0 motion-reduce:animate-none animate-login-grid-drift',
            isLight ? 'opacity-20' : 'opacity-50',
          )}
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(34, 211, 238, 0.06) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(34, 211, 238, 0.06) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
          aria-hidden
        />
        <div
          className={cn(
            'pointer-events-none absolute -right-20 top-1/4 h-48 w-48 rounded-full blur-[80px] motion-reduce:animate-none animate-login-neon-pulse',
            isLight ? 'bg-cyan-300/25' : 'bg-cyan-500/20',
          )}
        />
        <div
          className={cn(
            'pointer-events-none absolute -left-16 bottom-1/3 h-40 w-40 rounded-full blur-[72px] motion-reduce:animate-none animate-login-neon-pulse [animation-delay:1s]',
            isLight ? 'bg-fuchsia-300/20' : 'bg-fuchsia-500/15',
          )}
        />

        <div className="relative z-10 flex h-full flex-col">
          {useDynamicPlatformNav ? (
            <PlatformSidebarNav
              tree={platformSidebarTree!}
              onNavigate={onClose}
              isLight={isLight}
            />
          ) : (
            <>
              <div className="flex h-16 items-center border-b border-white/10 px-6">
                <BrandLogo isLight={isLight} />
              </div>

              <nav className="flex-1 space-y-1 overflow-y-auto py-4">
                {isAdmin ? (
                  <>
                    {mainVisible.length === 0 && systemVisible.length === 0 ? (
                      <p className="mx-4 px-3 text-xs text-zinc-500">
                        当前账号暂无可见菜单权限，请联系管理员。
                      </p>
                    ) : (
                      <>
                        {mainVisible.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={onClose}
                            className={linkClass}
                          >
                            {item.icon}
                            {item.label}
                          </NavLink>
                        ))}

                        {systemVisible.length > 0 && (
                          <div
                            className={cn(
                              'mx-3 mt-2 overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]',
                              systemSectionActive && 'border-cyan-500/20',
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => setSystemMgmtOpen((o) => !o)}
                              className={cn(
                                'flex w-full items-center justify-between gap-2 rounded-t-xl px-3 py-2.5 text-left transition-all duration-200 motion-reduce:transition-none',
                                'text-sm font-medium',
                                'border border-transparent text-zinc-400',
                                'hover:border-white/5 hover:bg-white/[0.04] hover:text-zinc-100',
                                systemSectionActive &&
                                  'border-cyan-500/25 bg-cyan-500/[0.08] text-zinc-200',
                              )}
                              aria-expanded={systemMgmtOpen}
                              aria-controls="sidebar-system-mgmt-items"
                            >
                              <span className="flex min-w-0 items-center gap-3">
                                <Cog className="h-5 w-5 shrink-0" aria-hidden />
                                系统管理
                              </span>
                              <ChevronDown
                                className={cn(
                                  'h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200',
                                  systemMgmtOpen ? 'rotate-0' : '-rotate-90',
                                )}
                                aria-hidden
                              />
                            </button>
                            {systemMgmtOpen && (
                              <div
                                id="sidebar-system-mgmt-items"
                                className="space-y-0.5 border-t border-white/[0.06] px-0 pb-1.5 pt-0.5"
                              >
                                {systemVisible.map((item) => (
                                  <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={onClose}
                                    className={({ isActive }) =>
                                      cn(
                                        linkClass({ isActive }),
                                        'mx-2 border-l-2 border-transparent pl-3',
                                        isActive && 'border-l-cyan-400/70',
                                      )
                                    }
                                  >
                                    {item.icon}
                                    {item.label}
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : tenantMainVisible.length === 0 &&
                  tenantSystemVisible.length === 0 ? (
                  <p className="mx-4 px-3 text-xs text-zinc-500">
                    当前账号暂无可见菜单权限，请联系管理员。
                  </p>
                ) : (
                  <>
                    {tenantMainVisible.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={linkClass}
                      >
                        {item.icon}
                        {item.label}
                      </NavLink>
                    ))}

                    {tenantSystemVisible.length > 0 && (
                      <div
                        className={cn(
                          'mx-3 mt-2 overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]',
                          tenantSystemActive && 'border-cyan-500/20',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setTenantSystemMgmtOpen((o) => !o)}
                          className={cn(
                            'flex w-full items-center justify-between gap-2 rounded-t-xl px-3 py-2.5 text-left transition-all duration-200 motion-reduce:transition-none',
                            'text-sm font-medium',
                            'border border-transparent text-zinc-400',
                            'hover:border-white/5 hover:bg-white/[0.04] hover:text-zinc-100',
                            tenantSystemActive &&
                              'border-cyan-500/25 bg-cyan-500/[0.08] text-zinc-200',
                          )}
                          aria-expanded={tenantSystemMgmtOpen}
                          aria-controls="sidebar-tenant-system-mgmt-items"
                        >
                          <span className="flex min-w-0 items-center gap-3">
                            <Cog className="h-5 w-5 shrink-0" aria-hidden />
                            系统管理
                          </span>
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200',
                              tenantSystemMgmtOpen ? 'rotate-0' : '-rotate-90',
                            )}
                            aria-hidden
                          />
                        </button>
                        {tenantSystemMgmtOpen && (
                          <div
                            id="sidebar-tenant-system-mgmt-items"
                            className="space-y-0.5 border-t border-white/[0.06] px-0 pb-1.5 pt-0.5"
                          >
                            {tenantSystemVisible.map((item) => (
                              <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.path === '/team'}
                                onClick={onClose}
                                className={({ isActive }) =>
                                  cn(
                                    linkClass({ isActive }),
                                    'mx-2 border-l-2 border-transparent pl-3',
                                    isActive && 'border-l-cyan-400/70',
                                  )
                                }
                              >
                                {item.icon}
                                {item.label}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </nav>

              <div className="relative z-10 border-t border-white/10 p-4">
                <p className="text-[11px] leading-relaxed text-zinc-500">
                  SystemHub v2.0
                  <span className="mt-1 block text-zinc-600">© 2026</span>
                </p>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
